import tocbot from 'tocbot';

const TOC_HEADING_SELECTOR = 'h2, h3, h4';
const FLOATING_BREAKPOINT = 1520;
const ENTRANCE_DELAY = 1000;
const DESKTOP_COLLAPSED_KEY = 'cavom-post-toc-collapsed';
const DESKTOP_TOC_LABEL_SHOW = 'Inhoudsopgave tonen';
const DESKTOP_TOC_LABEL_HIDE = 'Inhoudsopgave verbergen';

function getHeaderScrollOffset() {
	const header = document.querySelector( '.o-header' );

	if ( ! header ) {
		return 138;
	}

	return Math.ceil( header.getBoundingClientRect().bottom );
}

function setHeaderScrollOffset() {
	document.documentElement.style.setProperty(
		'--header-scroll-offset',
		`${ getHeaderScrollOffset() }px`
	);
}

function slugifyHeading( text ) {
	return text
		.trim()
		.toLowerCase()
		.normalize( 'NFD' )
		.replace( /[\u0300-\u036f]/g, '' )
		.replace( /[^a-z0-9\s-]/g, '' )
		.replace( /\s+/g, '-' )
		.replace( /-+/g, '-' )
		.replace( /^-|-$/g, '' );
}

function ensureHeadingIds( container ) {
	const usedIds = new Set();

	container.querySelectorAll( '[id]' ).forEach( ( element ) => {
		if ( element.id ) {
			usedIds.add( element.id );
		}
	} );

	container.querySelectorAll( TOC_HEADING_SELECTOR ).forEach( ( heading ) => {
		if ( heading.id ) {
			return;
		}

		let id = slugifyHeading( heading.textContent || '' );

		if ( ! id ) {
			id = 'section';
		}

		let uniqueId = id;
		let suffix = 2;

		while ( usedIds.has( uniqueId ) ) {
			uniqueId = `${ id }-${ suffix }`;
			suffix += 1;
		}

		heading.id = uniqueId;
		usedIds.add( uniqueId );
	} );
}

function scrollToElement( element ) {
	if ( ! element ) {
		return;
	}

	const top = element.getBoundingClientRect().top + window.scrollY - getHeaderScrollOffset();

	window.scrollTo( {
		top,
		behavior: 'smooth',
	} );
}

function getArticleTopElement() {
	return document.querySelector( '.m-pageTitle' ) || document.querySelector( '.flexible-contentWrap' );
}

function isDesktopToc() {
	return window.innerWidth >= FLOATING_BREAKPOINT;
}

function initDesktopTocCollapse( tocRoot ) {
	const handle = tocRoot.querySelector( '.js-postTocHandle' );

	if ( ! handle ) {
		return;
	}

	const updateHandle = () => {
		if ( ! isDesktopToc() ) {
			return;
		}

		const collapsed = tocRoot.classList.contains( '-collapsed' );

		handle.setAttribute( 'aria-expanded', collapsed ? 'false' : 'true' );
		handle.setAttribute(
			'aria-label',
			collapsed ? DESKTOP_TOC_LABEL_SHOW : DESKTOP_TOC_LABEL_HIDE
		);
	};

	if ( isDesktopToc() && localStorage.getItem( DESKTOP_COLLAPSED_KEY ) === '1' ) {
		tocRoot.classList.add( '-collapsed' );
	}

	handle.addEventListener( 'click', () => {
		if ( ! isDesktopToc() ) {
			return;
		}

		tocRoot.classList.toggle( '-collapsed' );
		localStorage.setItem(
			DESKTOP_COLLAPSED_KEY,
			tocRoot.classList.contains( '-collapsed' ) ? '1' : '0'
		);
		updateHandle();
	} );

	window.addEventListener( 'resize', () => {
		if ( ! isDesktopToc() ) {
			tocRoot.classList.remove( '-collapsed' );
		}

		updateHandle();
	} );

	updateHandle();
}

function initTocbot() {
	const tocElement = document.querySelector( '.js-toc' );
	const contentElement = document.querySelector( '.flexible-content' );

	if ( ! tocElement || ! contentElement ) {
		return;
	}

	ensureHeadingIds( contentElement );
	setHeaderScrollOffset();

	const scrollOffset = getHeaderScrollOffset();

	tocbot.init( {
		tocSelector: '.js-toc',
		contentSelector: '.flexible-content',
		headingSelector: TOC_HEADING_SELECTOR,
		hasInnerContainers: true,
		scrollSmooth: false,
		headingsOffset: scrollOffset,
		onClick( event ) {
			event.preventDefault();

			const href = event.currentTarget.getAttribute( 'href' );

			if ( ! href || href === '#' ) {
				return;
			}

			const heading = document.querySelector( href );

			if ( ! heading ) {
				return;
			}

			scrollToElement( heading );
		},
	} );

	window.addEventListener( 'resize', setHeaderScrollOffset, { passive: true } );
}

function initPostTocVisibility() {
	const tocRoot = document.querySelector( '.js-postToc' );
	const content = document.querySelector( '.flexible-content' );
	const toggle = tocRoot?.querySelector( '.js-postTocToggle' );
	const topButton = tocRoot?.querySelector( '.js-postTocTop' );
	const topFooter = tocRoot?.querySelector( '.js-postTocFooter' );

	if ( ! tocRoot || ! content ) {
		return;
	}

	const setVisible = ( isVisible ) => {
		tocRoot.classList.toggle( '-visible', isVisible );

		if ( ! isVisible ) {
			tocRoot.classList.remove( '-open' );
			toggle?.setAttribute( 'aria-expanded', 'false' );
		}
	};

	let canShow = false;
	let isInView = false;

	const updateVisibility = () => {
		setVisible( canShow && isInView );
	};

	setTimeout( () => {
		canShow = true;
		updateVisibility();
	}, ENTRANCE_DELAY );

	const observer = new IntersectionObserver(
		( [ entry ] ) => {
			isInView = entry.isIntersecting;
			updateVisibility();
		},
		{
			rootMargin: `-${ getHeaderScrollOffset() }px 0px 0px 0px`,
			threshold: 0,
		}
	);

	observer.observe( content );

	initDesktopTocCollapse( tocRoot );

	if ( toggle ) {
		toggle.addEventListener( 'click', () => {
			const isOpen = tocRoot.classList.toggle( '-open' );
			toggle.setAttribute( 'aria-expanded', isOpen ? 'true' : 'false' );
		} );
	}

	if ( topButton && topFooter ) {
		const firstHeading = content.querySelector( TOC_HEADING_SELECTOR );

		const updateTopButtonVisibility = () => {
			if ( ! firstHeading ) {
				topFooter.classList.remove( '-visible' );
				topButton.setAttribute( 'tabindex', '-1' );
				return;
			}

			const hasScrolledPastFirst = firstHeading.getBoundingClientRect().top < getHeaderScrollOffset();
			topFooter.classList.toggle( '-visible', hasScrolledPastFirst );
			topButton.setAttribute( 'tabindex', hasScrolledPastFirst ? '0' : '-1' );
		};

		window.addEventListener( 'scroll', updateTopButtonVisibility, { passive: true } );
		window.addEventListener( 'resize', updateTopButtonVisibility, { passive: true } );
		updateTopButtonVisibility();

		topButton.addEventListener( 'click', () => {
			scrollToElement( getArticleTopElement() );

			if ( window.innerWidth < FLOATING_BREAKPOINT ) {
				tocRoot.classList.remove( '-open' );
				toggle?.setAttribute( 'aria-expanded', 'false' );
			}
		} );
	}

	tocRoot.addEventListener( 'click', ( event ) => {
		if ( window.innerWidth >= FLOATING_BREAKPOINT || ! event.target.closest( '.toc-link, .js-postTocTop' ) ) {
			return;
		}

		tocRoot.classList.remove( '-open' );
		toggle?.setAttribute( 'aria-expanded', 'false' );
	} );

	document.addEventListener( 'click', ( event ) => {
		if ( window.innerWidth >= FLOATING_BREAKPOINT || ! tocRoot.classList.contains( '-open' ) ) {
			return;
		}

		if ( ! tocRoot.contains( event.target ) ) {
			tocRoot.classList.remove( '-open' );
			toggle?.setAttribute( 'aria-expanded', 'false' );
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Escape' && tocRoot.classList.contains( '-open' ) ) {
			tocRoot.classList.remove( '-open' );
			toggle?.setAttribute( 'aria-expanded', 'false' );
		}
	} );
}

function initPostToc() {
	const tocRoot = document.querySelector( '.js-postToc' );
	const contentElement = document.querySelector( '.flexible-content' );

	if ( ! tocRoot || ! contentElement ) {
		return;
	}

	if ( ! contentElement.querySelector( TOC_HEADING_SELECTOR ) ) {
		tocRoot.remove();
		return;
	}

	initTocbot();
	initPostTocVisibility();
}

export default initPostToc;
